import { createApi } from '@reduxjs/toolkit/query/react'

import { baseQueryWithReAuth } from './base-query'
import type { OkResponse } from './types/common'
import type {
	CreateRentalBody,
	ListRentalsParams,
	ListRentalsResponse,
	Rental,
	StartRentalBody,
	StartRentalResponse,
	UpdateRentalBody,
	UpdateRentalStatusBody,
} from './types/rental'

export const rentalsApi = createApi({
	reducerPath: 'rentalsApi',
	baseQuery: baseQueryWithReAuth,
	tagTypes: ['Rental'],
	endpoints: (builder) => ({
		listRentals: builder.query<ListRentalsResponse, ListRentalsParams | void>({
			query: (params) => ({ url: '/rent', params: params ?? {} }),
			providesTags: (result) =>
				result
					? [
							...result.rentals.map((r) => ({ type: 'Rental' as const, id: r.id })),
							{ type: 'Rental' as const, id: 'LIST' },
						]
					: [{ type: 'Rental' as const, id: 'LIST' }],
		}),

		getRental: builder.query<Rental | null, string>({
			query: (id) => `/rent/${id}`,
			providesTags: (_r, _e, id) => [{ type: 'Rental', id }],
		}),

		createRental: builder.mutation<Rental | null, CreateRentalBody>({
			query: (body) => ({ url: '/rent', method: 'POST', body }),
			invalidatesTags: [{ type: 'Rental', id: 'LIST' }],
		}),

		/**
		 * Seamless start-rental orchestration (issue #248) — resolves/creates
		 * the tenant contact, creates the rental and runs follow-up steps in
		 * one call. Side effects on contacts/properties/deals live in other
		 * API slices; callers invalidate those caches themselves.
		 */
		startRental: builder.mutation<StartRentalResponse, StartRentalBody>({
			query: (body) => ({ url: '/rent/start', method: 'POST', body }),
			invalidatesTags: [{ type: 'Rental', id: 'LIST' }],
		}),

		updateRental: builder.mutation<Rental | null, { id: string; body: UpdateRentalBody }>({
			query: ({ id, body }) => ({ url: `/rent/${id}`, method: 'PATCH', body }),
			invalidatesTags: (_r, _e, { id }) => [
				{ type: 'Rental', id },
				{ type: 'Rental', id: 'LIST' },
			],
		}),

		updateRentalStatus: builder.mutation<
			Rental | null,
			{ id: string; body: UpdateRentalStatusBody }
		>({
			query: ({ id, body }) => ({ url: `/rent/${id}/status`, method: 'PATCH', body }),
			invalidatesTags: (_r, _e, { id }) => [
				{ type: 'Rental', id },
				{ type: 'Rental', id: 'LIST' },
			],
		}),

		deleteRental: builder.mutation<OkResponse, string>({
			query: (id) => ({ url: `/rent/${id}`, method: 'DELETE' }),
			invalidatesTags: (_r, _e, id) => [
				{ type: 'Rental', id },
				{ type: 'Rental', id: 'LIST' },
			],
		}),
	}),
})

export const {
	useListRentalsQuery,
	useGetRentalQuery,
	useCreateRentalMutation,
	useStartRentalMutation,
	useUpdateRentalMutation,
	useUpdateRentalStatusMutation,
	useDeleteRentalMutation,
} = rentalsApi
